// Shared IP location detection service
export class LocationService {
  static instance = null;
  
  constructor() {
    this.cache = {
      data: null,
      timestamp: null,
      expiry: 5 * 60 * 1000 // 5 minutes cache
    };
    this.listeners = new Set();
    this.pendingFetch = null; // Prevent duplicate simultaneous fetches
  }
  
  static getInstance() {
    if (!LocationService.instance) {
      LocationService.instance = new LocationService();
    }
    return LocationService.instance;
  }
  
  // Add listener for location updates
  addListener(callback) {
    this.listeners.add(callback);
    // If we already have cached data, immediately call the callback
    if (this.cache.data) {
      callback(this.cache.data);
    }
  }
  
  // Remove listener
  removeListener(callback) {
    this.listeners.delete(callback);
  }
  
  // Notify all listeners of location update
  notifyListeners(data) {
    this.listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in location listener:', error);
      }
    });
  }
  
  // Check if cached data is still valid
  isCacheValid() {
    return this.cache.data && 
           this.cache.timestamp && 
           (Date.now() - this.cache.timestamp) < this.cache.expiry;
  }
  
  // Get cached data if valid, otherwise fetch new data
  async getLocationData(forceRefresh = false) {
    if (!forceRefresh && this.isCacheValid()) {
      console.log('Using cached location data:', this.cache.data);
      return this.cache.data;
    }
    
    // If there's already a fetch in progress, wait for it
    if (this.pendingFetch) {
      console.log('Location fetch already in progress, waiting...');
      return this.pendingFetch;
    }
    
    console.log('Fetching fresh location data...');
    this.pendingFetch = this.fetchLocationData().finally(() => {
      this.pendingFetch = null;
    });
    
    return this.pendingFetch;
  }
  
  // Morocco city mapping based on timezone/region for fallback
  getMoroccoCityFromTimezone(timezone, region) {
    if (!timezone) return null;
    
    const timezoneLower = timezone.toLowerCase();
    const regionLower = region ? region.toLowerCase() : '';
    
    // Map timezones and regions to major Moroccan cities
    const cityMap = {
      'casablanca': 'Casablanca',
      'rabat': 'Rabat',
      'tanger': 'Tangier',
      'tangier': 'Tangier',
      'tetouan': 'Tetouan',
      'tétouan': 'Tetouan',
      'marrakech': 'Marrakech',
      'marrakesh': 'Marrakech',
      'agadir': 'Agadir',
      'fes': 'Fez',
      'fez': 'Fez',
      'meknes': 'Meknes',
      'oujda': 'Oujda',
      'kenitra': 'Kenitra',
      'safi': 'Safi',
      'nador': 'Nador',
      'ouarzazate': 'Ouarzazate',
      'settat': 'Settat',
      'taza': 'Taza',
      'el jadida': 'El Jadida',
      'beni mellal': 'Beni Mellal',
      'khouribga': 'Khouribga',
      'mohammedia': 'Mohammedia',
      'temara': 'Temara'
    };
    
    // Check timezone name
    for (const [key, city] of Object.entries(cityMap)) {
      if (timezoneLower.includes(key) || regionLower.includes(key)) {
        return city;
      }
    }
    
    return null;
  }

  // Fetch location data from a single reliable IP service
  async fetchLocationData() {
    console.log('Starting IP location fetch...');
    
    // Use ipapi.co as the primary service (reliable and free)
    try {
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('ipapi.co response:', data);
        
        if (data && !data.error) {
          const parsed = {
            ip: data.ip,
            city: data.city,
            country: data.country_name,
            countryCode: data.country_code ? data.country_code.toUpperCase() : null,
            timezone: data.timezone,
            region: data.region,
            latitude: data.latitude,
            longitude: data.longitude,
            source: 'ipapi.co',
            timestamp: new Date().toISOString()
          };
          
          // If this is Morocco and we don't have a city, try to infer from timezone
          if (parsed.countryCode === 'MA' && !parsed.city && parsed.timezone) {
            const inferredCity = this.getMoroccoCityFromTimezone(parsed.timezone, parsed.region);
            if (inferredCity) {
              parsed.city = inferredCity;
              console.log(`Inferred Morocco city from timezone: ${inferredCity}`);
            }
          }
          
          this.cache.data = parsed;
          this.cache.timestamp = Date.now();
          
          console.log('Successfully fetched location data:', this.cache.data);
          
          // Notify all listeners
          this.notifyListeners(this.cache.data);
          
          return this.cache.data;
        }
      } else {
        console.log(`ipapi.co returned status ${response.status}`);
      }
    } catch (error) {
      console.log('ipapi.co failed:', error.message);
    }
    
    // Fallback: try ipwho.is
    try {
      const response = await fetch('https://ipwho.is/', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('ipwho.is response:', data);
        
        if (data && !data.error) {
          const parsed = {
            ip: data.ip,
            city: data.city,
            country: data.country,
            countryCode: data.country_code ? data.country_code.toUpperCase() : null,
            timezone: data.timezone?.id || data.timezone,
            region: data.region,
            latitude: data.latitude,
            longitude: data.longitude,
            source: 'ipwho.is',
            timestamp: new Date().toISOString()
          };
          
          // If this is Morocco and we don't have a city, try to infer from timezone
          if (parsed.countryCode === 'MA' && !parsed.city && parsed.timezone) {
            const inferredCity = this.getMoroccoCityFromTimezone(parsed.timezone, parsed.region);
            if (inferredCity) {
              parsed.city = inferredCity;
              console.log(`Inferred Morocco city from timezone: ${inferredCity}`);
            }
          }
          
          this.cache.data = parsed;
          this.cache.timestamp = Date.now();
          
          console.log('Successfully fetched location data (fallback):', this.cache.data);
          
          // Notify all listeners
          this.notifyListeners(this.cache.data);
          
          return this.cache.data;
        }
      } else {
        console.log(`ipwho.is returned status ${response.status}`);
      }
    } catch (error) {
      console.log('ipwho.is failed:', error.message);
    }
    
    // Final fallback - just get IP
    try {
      console.log('All geolocation services failed, trying basic IP...');
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      
      if (ipData.ip) {
        const fallbackData = {
          ip: ipData.ip,
          city: null,
          country: null,
          countryCode: null,
          timezone: null,
          region: null,
          latitude: null,
          longitude: null,
          source: 'ipify.org (IP only)',
          timestamp: new Date().toISOString()
        };
        
        // Cache the fallback result
        this.cache.data = fallbackData;
        this.cache.timestamp = Date.now();
        
        console.log('Got basic IP:', fallbackData);
        
        // Notify all listeners
        this.notifyListeners(this.cache.data);
        
        return fallbackData;
      }
    } catch (ipError) {
      console.log('Basic IP fetch failed:', ipError.message);
    }
    
    console.log('All location services failed');
    
    // Return null data but still notify listeners
    const errorData = {
      ip: null,
      city: null,
      country: null,
      countryCode: null,
      timezone: null,
      region: null,
      latitude: null,
      longitude: null,
      source: 'failed',
      timestamp: new Date().toISOString(),
      error: 'All location services failed'
    };
    
    this.cache.data = errorData;
    this.cache.timestamp = Date.now();
    
    this.notifyListeners(errorData);
    
    return errorData;
  }
  
  // Clear cache (useful for testing or manual refresh)
  clearCache() {
    this.cache.data = null;
    this.cache.timestamp = null;
  }
}

// Export singleton instance
export const locationService = LocationService.getInstance();

// Export hook for React components
export const useLocationService = () => {
  return locationService;
};
